/**
 * Grants — one page, one spine (`/bills`, rebuilt 2026-09-21 from
 * docs/superpowers/specs/2026-09-21-parent-chrome-and-grants-design.md).
 *
 * Victor, on the page this replaces: "The page /bills is super confusing.
 * Are we managing/applying for grants? Is it a dashboard? Why do we have a
 * expense tab and we are showing expenses. Let's subtract for clarity but
 * also try to make sense of the job to be done here. Let's create clarity
 * around applying/renewing/managing and view activity how much of a grant
 * have I consumed."
 *
 * THE JOB, STATED ONCE
 *   What TRF money do I have, what must I do to keep it, and how much of it
 *   is left?
 *
 * WHY IT CONFUSED. All three grant verbs lived somewhere else — a new
 * application is `/apply/wizard`, its progress `/apply/status`, the
 * Signature renewal `/renew` — so this page held only the aftermath. It was
 * called Finances, which promises household budgeting, and its second tab
 * was a reimbursement ledger reading as personal expense tracking with no
 * visible connection to the grant the money came from. The third tab was
 * named after a vendor.
 *
 * THE SPINE, top to bottom (re-ordered 2026-10-03 — status first):
 *   1. header         eyebrow "Grants" + one toolbar (+ · search · filter).
 *   2. status line    "All on track" or "N receipts need you ↓", what TRF has
 *                     paid, and the next Signature payment (`GrantsStatusLine`).
 *   3. Your grants    one line each: type, status, how much is left, and a
 *                     to-do count. Denied/withdrawn fold into "Past grants".
 *   4. Finish these receipts   EVERY receipt waiting on the family, linked
 *                     to a grant or not. Absent when there is none.
 *   5. renewal        one line: the invitation, only in the final 3 months
 *                     of an approved Signature grant or while a renewal
 *                     draft is open; once a renewal is in, its status line
 *                     (received / under review / declined / waitlisted), no button (#2331).
 *   6. With TRF       unlinked receipts sent in and not yet decided (#2658).
 *                     Absent when there is none.
 *   7. Past receipts  unlinked, decided history — collapsed.
 *   8. How payments work  TRF's payment rules, always shown (#2956).
 *
 * Victor, 2026-10-03, on the order before this one: "I am not sure where to
 * start. Do I renew the grant? Why am I being reminded every time the page is
 * loaded? … I am expecting to see the list of my grants and the status —
 * everything good? Do I need any action? Where is my money?" The renewal was
 * shown to every family with no renewal on file, whatever their grant's age;
 * "Needs a grant" held receipts needing a photo or an answer, not a grant; and
 * "Other receipts … Nothing is needed from you" asked why it was there at all.
 *
 * The 3/4/5 split came out of the production walk (2026-09-21). "Needs a
 * grant" originally held EVERY unlinked receipt, so a family that had never
 * linked one — linking is optional, so most — got months of Paid back and
 * Approved rows hoisted above their grants under a heading that said they
 * needed one. A settled receipt needs nothing, and the grants are what the
 * page is called.
 *
 * WHAT LEFT
 *   - The tab row. `?tab=` is no longer a store; a historical `?tab=grants`
 *     or `?tab=expenses` link simply lands on the page.
 *   - `Bill.com (test)`. Staff machinery behind a role check on a FAMILY
 *     surface fails §1.9. `BillcomTestTab` had no importer left and was
 *     deleted in #2109 R3 (R3-B18: dead code is deleted, not translated).
 *   - The `RenewalCard` band, compressed into the one-line action — through
 *     the same `apply.renewal.*` copy slots, so the live store stays valid.
 *
 * Nothing was deleted from the ledger: the month-by-month history moved
 * inside the grant it belongs to (`GrantReceipts`), and the family's own
 * to-do stayed at page level, which is the level each answers a question at.
 *
 * The /bills URL and the persisted `bills` menu key survive unchanged —
 * labels rename, keys never (§2.8).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { ConfirmActionDialog } from '@/components/ui/ConfirmActionDialog';
import { FinancesHeader } from '@/components/bills/FinancesChrome';
import { GrantsPanel } from '@/components/grants/GrantsPanel';
import { GrantsStatusLine } from '@/components/grants/GrantsStatusLine';
import { HowPaymentsWork } from '@/components/grants/HowPaymentsWork';
import { ExpenseLedger, CLAIMS_QUERY_KEY } from '@/components/expenses/ExpenseLedger';
import { ReceiptCapture } from '@/components/expenses/ReceiptCapture';
import { ExpenseDetailsDialog, type ExpenseFormValues } from '@/components/expenses/ExpenseDetailsDialog';
import { PendingDecision, SectionTitle } from '@/components/editorial';
import { RenewalStatusText } from '@/components/grants/RenewalCard';
import { useCopy } from '@/hooks/useCopy';
import {
  deriveRenewalCta,
  renewalCtaFromError,
  renewalCtaLabelKey,
  renewalYearMsgFromApp,
} from '@/lib/renewal-display';
import {
  claimMatches,
  financesStateFromParams,
  receiptSlice,
  writeFinancesState,
  type CategoryScope,
  type ClaimScope,
  type FinancesFilters,
} from '@/lib/bills-display';
import { signatureRenewalDue } from '@/lib/grants-status';
import { askedItemNames, useFlaggedFieldLabels } from '@/lib/flagged-fields';
import { formatList } from '@/i18n/intl';
import { useMsg } from '@/i18n/msg';
import { RENEWAL_MINE_QUERY_KEY } from '@/lib/wizardDataSource';
import { extractApiErrorMessage, localizedApiErrorMessage } from '@/lib/utils';
import { isUnsupportedFileTypeError } from '@/lib/uploadTypes';
import { api } from '@/services/api';
import type { GrantRequest, ReimbursementClaim } from '@/types/api';

/**
 * The seasonal action, as ONE line (spec Part 3 item 2).
 *
 * `deriveRenewalCta` already computes the state this needs. Only the chrome
 * changed from the card: a bordered band with a heading and a paragraph
 * became a line, because an action you may not even have today should not
 * out-weigh the grants themselves.
 *
 * What each state shows:
 *   - none (fresh invitation, only when due) / editable → the invite + button.
 *   - needs_info (TRF sent it back, #2527) → "TRF needs something", the
 *     items it asked about by name, and the button.
 *   - pending (submitted, under review, held) → a status LINE, no button
 *     (#2331). Inviting a family to renew twice is the defect the "invite
 *     nothing" arm exists to prevent, and it is kept — but "offer nothing"
 *     had also left the family with no confirmation that its renewal arrived.
 *   - declined → not approved, ask the case manager; no button
 *     (DECISIONS 2026-10-11, superseding #1490's neutral line).
 *   - waitlisted (its notice sent) → on the waitlist and what happens next;
 *     no button (DECISIONS 2026-10-11).
 *   - handed_off (approved, grant created) → nothing: the grant speaks.
 *   - unknown (the read failed) → nothing: a failed request is not evidence.
 * The pending line ends on its own when the renewal's status changes.
 *
 * Copy resolves through the same `apply.renewal.*` slots the card used, and
 * the status sentence is the card's own `RenewalStatusText`, so the live copy
 * store stays valid and `RenewalCardView` remains an honest copy-editor
 * preview of what the family reads (D-CS-12).
 */
function RenewalActionLine() {
  const navigate = useNavigate();
  const { copy, ready } = useCopy();
  const { t, i18n } = useTranslation('grants');
  const resolve = useMsg();
  const { data, isError, error, isPending } = useQuery({
    // Shared with the wizard's submit, which writes the submitted renewal
    // into this key before it navigates here (#2332).
    queryKey: RENEWAL_MINE_QUERY_KEY,
    queryFn: () => api.renewals.getMine(),
    retry: false,
  });
  // Same key GrantsPanel reads — a cache hit, not a second fetch.
  const grantsQuery = useQuery({ queryKey: ['grants-mine'], queryFn: () => api.grants.mine() });
  // #2527: the asked-about questions' labels, fetched only while TRF waits.
  const flagLabel = useFlaggedFieldLabels(isError ? null : data?.data ?? null);

  // Hold paint until the copy read settles, so a family never sees the seed
  // replaced by TRF's wording mid-read (RenewalCard guardrail 7).
  //
  // #2702: latched. After a FAILED copy read the query has no data, so the
  // status line's own `useCopy()` refetches on mount, `ready` drops back to
  // false, this line unmounts its child, the read fails, and the child mounts
  // again: about 1,400 requests in 10 seconds, until the public limiter
  // answers 429 and keeps the loop alive. Once the read has settled once, the
  // line stays painted (the FormDefinitionGate precedent).
  const readyOnce = useRef(false);
  if (ready) readyOnce.current = true;
  if (!readyOnce.current) return null;
  // Until the renewal read settles the page knows nothing. Deriving from
  // `undefined` would read as "no renewal" and paint "Start your renewal" for
  // a family whose renewal is already in.
  if (isPending) return null;

  // #2659: the grants decide whether a decided renewal's year is over, so the
  // next year's renewal is offered beneath it.
  const state = isError
    ? renewalCtaFromError(error)
    : deriveRenewalCta(data?.data ?? null, grantsQuery.isSuccess ? (grantsQuery.data?.data ?? []) as GrantRequest[] : null);
  // `unknown` (the fetch failed) offers nothing: a failed request is not
  // evidence that a family has no renewal. `handed_off`: the grant speaks.
  if (state.kind === 'unknown' || state.kind === 'handed_off') return null;
  // A fresh invitation only when it is due (Victor, 2026-10-03): the final 3
  // months of an approved Signature grant. Before this every family with no
  // renewal on file was invited on every visit — including one whose grant
  // had just started. A draft already under way is always offered back.
  if (state.kind === 'none') {
    const grants = (grantsQuery.data?.data ?? []) as GrantRequest[];
    if (!grantsQuery.isSuccess || !signatureRenewalDue(grants)) return null;
  }

  // "Year 2" is one key with the number (#2109 R3-B3).
  // A fresh start names no year: the record on file (if any) is last year's.
  const year = state.kind === 'none' ? null : resolve(renewalYearMsgFromApp(data?.data ?? null)) || null;

  // #2331: once the renewal is in, the line confirms it — no button. Shown
  // whatever the grant's age: the due window gates only a fresh invitation.
  if (state.kind === 'pending' || state.kind === 'declined' || state.kind === 'waitlisted') {
    return (
      <div
        className="rounded-lg border border-stevens-indigo/30 bg-stevens-indigo/5 px-4 py-2.5"
        data-testid="renewal-status-line"
      >
        <RenewalStatusText state={state} yearText={year} className="text-body text-foreground" />
      </div>
    );
  }

  // #2527: TRF sent the renewal back. The invite ("Renew your Signature
  // Grant … It only takes a few minutes") told the family nothing was wanted;
  // this says what is, by name once the form's labels arrive.
  const asked = state.kind === 'needs_info' && data?.data ? askedItemNames(data.data, flagLabel, resolve, i18n.resolvedLanguage ?? i18n.language) : null;

  return (
    <div
      className="flex scroll-mt-20 flex-col items-start gap-2 rounded-lg border border-stevens-indigo/30 bg-stevens-indigo/5 px-4 py-2.5 sm:flex-row sm:items-center sm:gap-4"
      id={RENEWAL_ANCHOR}
      data-testid="renewal-action"
    >
      {/* Below `sm` the button stacks under the sentence, and its label may
          wrap: a translated label ("Commencer le renouvellement") beside the
          text squeezed it into a narrow column and overflowed the card. */}
      {state.kind === 'needs_info' ? (
        <p className="min-w-0 text-body text-foreground sm:flex-1" data-testid="renewal-needs-info">
          {t('renewal.needsInfo.line')}
          {asked && (
            <span className="text-stevens-ink-3">
              {' '}{t('renewal.needsInfo.asked', { items: formatList(asked, { type: 'conjunction' }) })}
            </span>
          )}
        </p>
      ) : (
        <p className="min-w-0 text-body text-foreground sm:flex-1">
          {copy('apply.renewal.card_heading')}
          {/* The space before each suffix is layout, not copy: a catalog value
              never leans on leading whitespace (a trimming translator drops it). */}
          {year && <span className="text-stevens-indigo">{' '}{t('renewal.yearSuffix', { year })}</span>}
          <span className="text-stevens-ink-3">{' '}{t('renewal.inviteSuffix', { line: copy('apply.renewal.invite_line') })}</span>
        </p>
      )}
      <Button
        size="sm"
        variant="brand"
        className="h-auto min-h-9 max-w-full whitespace-normal py-1.5 text-start sm:shrink-0"
        onClick={() => navigate('/renew')}
      >
        {resolve({ key: renewalCtaLabelKey(state) })}
      </Button>
    </div>
  );
}

/** Where the status line's "N receipts need you ↓" lands. */
const TODO_ANCHOR = 'receipts-todo';
/** Where the status line's "Your renewal needs you ↓" lands. */
const RENEWAL_ANCHOR = 'renewal-action';
/** Where the capture line's See it lands for a receipt TRF has not decided (#2658). */
const WITH_TRF_ANCHOR = 'receipts-with-trf';
/** Where the capture line's See it lands for a settled receipt (#2378). */
const PAST_ANCHOR = 'past-receipts';

/**
 * A receipts section around one `ExpenseLedger` slice.
 *
 * `todo` is "Finish these receipts": every receipt waiting on the family
 * (2026-10-03 — it was "Needs a grant", unlinked only, although none of the
 * four attention states is about a grant). `with_trf` is the unlinked
 * receipts TRF has and has not decided (#2658) — open, because the family is
 * waiting on them. `history` is the unlinked decided receipts, folded shut:
 * nothing in it asks anything of the family, so it should not ask for their
 * attention either. `receiptSlice` files each claim in exactly one.
 *
 * It reads the SAME query key the ledger reads, so this is a cache hit and
 * not a second fetch, and the frame and its contents can never disagree
 * about whether there is anything to show. Nothing renders while the fetch
 * is in flight or if it fails: an empty frame is a section announcing an
 * empty room, and the ledger itself already owns the error state.
 */
function ReceiptSection({
  kind,
  search,
  claimScope,
  categoryScope,
  open,
  onOpenChange,
  children,
}: {
  kind: 'todo' | 'with_trf' | 'history';
  /** `history` only: the fold is controlled, so See it can open it (#2378). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The toolbar's search and receipt filters, so the count and the empty line match what the ledger shows (#2407). */
  search: string;
  claimScope: ClaimScope;
  categoryScope: CategoryScope;
  children: ReactNode;
}) {
  const { t } = useTranslation('grants');
  const { data } = useQuery({
    queryKey: CLAIMS_QUERY_KEY,
    queryFn: () => api.reimbursements.listMine(),
  });
  const rows = (data?.data ?? []) as ReimbursementClaim[];
  const slice = kind === 'history' ? 'past' : kind;
  const now = new Date();
  const mine = rows.filter((c) => receiptSlice(c, now) === slice);
  if (mine.length === 0) return null;

  // #2407: the heading counted every past receipt while a filter or a search
  // emptied the list under it, and the empty body said nothing. The count is
  // now the rows the toolbar leaves, and an emptied slice says so in one
  // line, the way "No grants match." does above. The section itself stays:
  // the receipts exist, the filter is hiding them.
  const at = new Date();
  const matched = mine.filter((c) => claimMatches(c, claimScope, categoryScope, search, at)).length;
  const body = matched === 0
    ? <p className="text-body text-stevens-ink-3" data-testid="receipts-no-match">{t('page.noReceiptsMatch')}</p>
    : children;

  if (kind === 'history') {
    return (
      <details
        className="space-y-2 scroll-mt-20"
        id={PAST_ANCHOR}
        data-testid="past-receipts"
        open={open}
        onToggle={(e) => onOpenChange?.(e.currentTarget.open)}
      >
        <summary className="cursor-pointer">
          <SectionTitle as="h2" level="card-title" className="inline">
            {t('page.pastReceipts.title', { count: matched })}
          </SectionTitle>
        </summary>
        <p className="text-body-sm text-stevens-ink-3">{t('page.pastReceipts.lede')}</p>
        {body}
      </details>
    );
  }

  if (kind === 'with_trf') {
    return (
      <section className="space-y-2 scroll-mt-20" id={WITH_TRF_ANCHOR} data-testid="receipts-with-trf">
        <SectionTitle as="h2" level="card-title">{t('page.withTrf.title', { count: matched })}</SectionTitle>
        <p className="text-body-sm text-stevens-ink-3">{t('page.withTrf.lede')}</p>
        {body}
      </section>
    );
  }

  return (
    <section className="space-y-2 scroll-mt-20" id={TODO_ANCHOR} data-testid="receipts-todo">
      <div className="flex items-center gap-1.5">
        <SectionTitle as="h2" level="card-title">{t('page.todo.title')}</SectionTitle>
        {/* The two open questions about this list's shape hang off the
            to-do heading (#2097). A marker is asked once, and
            `showMarkers={false}` on the history ledger keeps DOM ids unique. */}
        <PendingDecision id="expenses-ledger-shape" side="right" />
        <PendingDecision id="finances-upcoming-planner" side="right" />
      </div>
      <p className="text-body-sm text-stevens-ink-3">{t('page.todo.lede')}</p>
      {body}
    </section>
  );
}

export function BillsPage() {
  const { t, i18n } = useTranslation('grants');
  const { toast } = useToast();
  // R3-B11: the server's English sentence only for an English reader; any
  // other language reads the localized generic line.
  const errorText = (e: Error) =>
    i18n.language === 'en' || i18n.language.startsWith('en-')
      ? extractApiErrorMessage(e) || e.message
      : localizedApiErrorMessage(e, t, i18n.language);
  // #2380: a receipt the server refused by type names that reason in every
  // language, the same sentence the capture line shows — "try again" is wrong
  // for a file that can never pass.
  const { t: tMoney } = useTranslation('money');
  const saveErrorText = (e: Error) =>
    isUnsupportedFileTypeError(e) ? tMoney('capture.wrongType') : errorText(e);
  const queryClient = useQueryClient();

  // ── Toolbar state ──────────────────────────────────────────────────────
  // In the URL (#2407): a filtered page survives a reload and a shared link.
  // `replace`, so typing a search does not stack one history entry per
  // keystroke in front of the back button.
  const [params, setParams] = useSearchParams();
  const { filters, search } = useMemo(() => financesStateFromParams(params), [params]);
  const setFilters = (next: FinancesFilters) =>
    setParams((prev) => writeFinancesState(prev, next, financesStateFromParams(prev).search), { replace: true });
  const setSearch = (next: string) =>
    setParams((prev) => writeFinancesState(prev, financesStateFromParams(prev).filters, next), { replace: true });
  const [captureOpen, setCaptureOpen] = useState(false);

  // ── Dialogs ────────────────────────────────────────────────────────────
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsClaim, setDetailsClaim] = useState<ReimbursementClaim | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ReimbursementClaim | null>(null);

  // ── See it from the capture line (#2378) ───────────────────────────────
  // A scanned receipt is auto-submitted; it lands in "With TRF" (#2658; it
  // was filed in the folded "Past receipts" before, out of sight). A settled
  // one still opens the fold: it is controlled so the capture line's See it
  // can open it, and the scroll waits for the render that opened it.
  const [pastOpen, setPastOpen] = useState(false);
  const [seeIt, setSeeIt] = useState<{ anchor: string; claim: ReimbursementClaim } | null>(null);
  useEffect(() => {
    if (!seeIt) return;
    const el = document.getElementById(seeIt.anchor);
    if (el) {
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // The section is not on the page yet (the list has not refetched):
      // open the receipt itself rather than scroll to nothing.
      setDetailsClaim(seeIt.claim);
      setDetailsOpen(true);
    }
    setSeeIt(null);
  }, [seeIt]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: CLAIMS_QUERY_KEY });

  // ── Mutations ──────────────────────────────────────────────────────────
  const saveExpense = useMutation({
    mutationFn: async ({ claim, values, file, reply }: { claim: ReimbursementClaim | null; values: ExpenseFormValues; file: File | null; reply?: string }) => {
      const body = {
        vendor: values.vendor?.trim() || null,
        amount: values.amount ?? null,
        expense_date: values.expense_date || null,
        category: values.category ?? null,
        note: values.note?.trim() || null,
      };
      if (!claim) {
        if (file) {
          // One call: the receipt and the facts together (POST /documents/upload).
          const form = new FormData();
          form.append('document', file);
          form.append('kind', 'receipt');
          for (const [k, v] of Object.entries(body)) if (v !== null && v !== undefined) form.append(k, String(v));
          return api.documents.upload(form);
        }
        return api.reimbursements.create(body);
      }
      // The reply (needs_info only) rides on the same PATCH and sends it back.
      // With a file, the PATCH defers the submit to attachReceipt: a needs_info
      // claim is already complete, so submitting first made the upload a 409.
      const updated = await api.reimbursements.update(claim._id, {
        ...body,
        ...(reply ? { family_reply: reply } : {}),
        ...(file ? { defer_submit: true } : {}),
      });
      if (file) {
        const form = new FormData();
        form.append('document', file);
        return api.reimbursements.attachReceipt(claim._id, form);
      }
      return updated;
    },
    onSuccess: (_res, vars) => {
      refresh();
      toast({
        title: vars.reply
          ? t('page.toast.replySent')
          : vars.claim ? t('page.toast.expenseUpdated') : t('page.toast.expenseAdded'),
      });
      setDetailsOpen(false);
      setDetailsClaim(null);
    },
    onError: (e: Error) => toast({ variant: 'destructive', title: t('page.toast.saveFailed'), description: saveErrorText(e) }),
  });

  const deleteExpense = useMutation({
    mutationFn: (id: string) => api.reimbursements.remove(id),
    onSuccess: () => { refresh(); toast({ title: t('page.toast.deleted') }); },
    onError: (e: Error) => toast({ variant: 'destructive', title: t('page.toast.deleteFailed'), description: errorText(e) }),
  });

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteExpense.mutate(pendingDelete._id);
    setPendingDelete(null);
  };

  // With one page there is no section to switch to first (#848 is satisfied
  // by construction rather than by a hop).
  const openCapture = () => setCaptureOpen(true);
  const openNewExpense = () => {
    setDetailsClaim(null);
    setDetailsOpen(true);
  };
  const openDetails = (claim: ReimbursementClaim) => {
    setDetailsClaim(claim);
    setDetailsOpen(true);
  };
  // Where a receipt lives on this page: the to-do while it needs the family,
  // the fold once it is settled and unlinked, inside its grant otherwise —
  // and a grant row is not this page's to open, so that one opens the details.
  const openClaim = (claim: ReimbursementClaim) => {
    const slice = receiptSlice(claim);
    if (slice === 'todo') {
      setSeeIt({ anchor: TODO_ANCHOR, claim });
    } else if (slice === 'with_trf') {
      setSeeIt({ anchor: WITH_TRF_ANCHOR, claim });
    } else if (slice === 'past') {
      setPastOpen(true);
      setSeeIt({ anchor: PAST_ANCHOR, claim });
    } else {
      openDetails(claim);
    }
  };

  // ── Page ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <FinancesHeader
        filters={filters}
        onFiltersChange={setFilters}
        search={search}
        onSearchChange={setSearch}
        onAddReceipt={openCapture}
        onAddExpense={openNewExpense}
      />

      {/* #2493: the "N receipts need you" count follows the toolbar, like the sections below. */}
      <GrantsStatusLine
        todoAnchor={TODO_ANCHOR}
        renewalAnchor={RENEWAL_ANCHOR}
        filters={{ search, claimScope: filters.claimScope, categoryScope: filters.categoryScope }}
      />

      {captureOpen && (
        <ReceiptCapture onUploaded={refresh} onOpenClaim={openClaim} onClose={() => setCaptureOpen(false)} />
      )}

      {/* The grants: the page's subject, read right after the verdict. */}
      <section className="space-y-2">
        <SectionTitle as="h2" level="card-title">{t('page.yourGrants')}</SectionTitle>
        <GrantsPanel
          search={search}
          statusScope={filters.grantScope}
          emptyMatch={<p className="text-body text-stevens-ink-3">{t('page.noGrantsMatch')}</p>}
        />
      </section>

      {/* Everything waiting on the family. Absent when there is nothing. */}
      <ReceiptSection kind="todo" search={search} claimScope={filters.claimScope} categoryScope={filters.categoryScope}>
        <ExpenseLedger
          scope="todo"
          headingAs="h3"
          showGroupHeadings={false}
          showMarkers={false}
          search={search}
          claimScope={filters.claimScope}
          categoryScope={filters.categoryScope}
          onOpen={openDetails}
          onEdit={openDetails}
          onDelete={setPendingDelete}
          onAddReceipt={openCapture}
        />
      </ReceiptSection>

      <RenewalActionLine />

      {/* Receipts TRF has and has not decided (#2658). Absent when none. */}
      <ReceiptSection kind="with_trf" search={search} claimScope={filters.claimScope} categoryScope={filters.categoryScope}>
        <ExpenseLedger
          scope="unlinked-with-trf"
          headingAs="h3"
          showMarkers={false}
          search={search}
          claimScope={filters.claimScope}
          categoryScope={filters.categoryScope}
          onOpen={openDetails}
          onEdit={openDetails}
          onDelete={setPendingDelete}
          onAddReceipt={openCapture}
        />
      </ReceiptSection>

      {/* Decided receipts with no grant — folded shut. */}
      <ReceiptSection
        kind="history"
        search={search}
        claimScope={filters.claimScope}
        categoryScope={filters.categoryScope}
        open={pastOpen}
        onOpenChange={setPastOpen}
      >
        <ExpenseLedger
          scope="unlinked-settled"
          headingAs="h3"
          showMarkers={false}
          search={search}
          claimScope={filters.claimScope}
          categoryScope={filters.categoryScope}
          onOpen={openDetails}
          onEdit={openDetails}
          onDelete={setPendingDelete}
          onAddReceipt={openCapture}
        />
      </ReceiptSection>

      {/* TRF's payment rules (#2956): reference, so it reads last. */}
      <HowPaymentsWork />

      <ExpenseDetailsDialog
        open={detailsOpen}
        onOpenChange={(o) => { setDetailsOpen(o); if (!o) setDetailsClaim(null); }}
        claim={detailsClaim}
        onSubmit={(values, file, reply) => saveExpense.mutate({ claim: detailsClaim, values, file, reply })}
        isSubmitting={saveExpense.isPending}
        onAddNew={openNewExpense}
      />

      {/* The in-app confirm (D-C) behind every row trash. Drafts only — the
          server refuses anything TRF has seen. */}
      <ConfirmActionDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={t('page.deleteDialog.title')}
        description={t('page.deleteDialog.description')}
        confirmLabel={t('page.deleteDialog.confirm')}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
